'use client';

import * as React from 'react';
import type { CSSProperties, SelectHTMLAttributes } from 'react';
import { JIcon } from '../../icons';
import { cx } from '../utils';
import { IS_PROD, devWarnValueUndefined } from '../internal/devWarn';
import type { SelectOption } from '../types';
import type { UnsafeProps } from '../escape-hatches';
import { Popover } from './Popover';

/* ═══════════════════════════════════════════════════
   Select — custom dropdown with listbox semantics
   APG: Listbox (https://www.w3.org/WAI/ARIA/apg/patterns/listbox/)

   Replaces the native <select> which is un-styleable on macOS.
   Uses `.j-menu` / `.j-menu-item` CSS classes for visual consistency
   with SplitButton / DropdownMenu, but uses `role="listbox"` /
   `role="option"` / `aria-selected` for correct ARIA semantics.
   ═══════════════════════════════════════════════════ */

export interface SelectProps
  extends
    Omit<
      SelectHTMLAttributes<HTMLSelectElement>,
      | 'children'
      | 'className'
      | 'defaultValue'
      | 'multiple'
      | 'onChange'
      | 'size'
      | 'style'
      | 'value'
    >,
    UnsafeProps {
  /** Currently selected value. */
  value?: string;
  /** Called with a native select-like change event when the user selects an option. */
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  /** Internal/additive string callback for composed primitives. */
  onValueChange?: (value: string) => void;
  /** Available options. */
  options: SelectOption[];
  /** Placeholder text shown when no value is selected. */
  placeholder?: string;
  /** Visual size variant. Routes through ADR-045 `--control-h`. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** Render an inline search row at the top of the dropdown. */
  search?: boolean;
  /** Placeholder for the optional search row. */
  searchPlaceholder?: string;
  /** Message shown when search filters out every option. */
  emptyMessage?: string;
  /** When true, applies error border styling. */
  error?: boolean;
  /** Additional class name on the trigger button. */
  className?: string;
  /** Inline style. Layout-axis properties (`width`, `minWidth`,
   *  `maxWidth`, `inlineSize`, `minInlineSize`, `maxInlineSize`, `flex`,
   *  `flexBasis`, `flexGrow`, `flexShrink`) are forwarded to the
   *  `.j-select-wrapper` outer element so percentage and `calc(...)`
   *  widths resolve against the wrapper's layout parent (v5.1.x hotfix
   *  — see component implementation comment on `splitLayoutStyle`).
   *  All other properties (color, marginTop, etc.) stay on the inner
   *  trigger button. */
  style?: CSSProperties;
}

function createSelectChangeEvent(
  optionValue: string,
  name: string | undefined,
): React.ChangeEvent<HTMLSelectElement> {
  const target = {
    name: name ?? '',
    value: optionValue,
  } as HTMLSelectElement;
  return {
    currentTarget: target,
    target,
  } as React.ChangeEvent<HTMLSelectElement>;
}

/* v5.1.x regression hotfix (Cabinet audit 2026-05-21):
   the `.j-select-wrapper` introduced in v5.1 is `display: inline-flex;
   width: auto` (see `src/css/kit.css` `.j-select-wrapper`). Before v5.1
   the inline `style.width` (and `style.flex` / `style.minWidth`) on a
   `<Select>` reached the trigger button directly, and the button was the
   element the adopter laid out against. With the wrapper in place the
   button is now nested, so a percentage-width or `calc(...)` width on
   the button resolves against the wrapper's content-sized track —
   circular, collapsing to min-content. Fixed pixel widths still work
   because they have no percentage to resolve.

   The fix is to forward the layout-axis subset of the adopter's
   `style` / `unsafe_style` (width / minWidth / maxWidth / flex /
   flexBasis / flexGrow / flexShrink, plus their logical-axis siblings)
   to the wrapper. The button keeps `width: 100%` via `.j-select-standalone`
   (kit.css), so the trigger continues to fill the wrapper bounds and the
   visual outcome matches v5.0.x. Non-layout style properties
   (marginTop, color, etc.) stay on the trigger button so unrelated
   semantics don't change.

   This is consistent with how the kit treats other inline-style escape
   hatches (Input forwards `unsafe_style` to the outer `.j-field`) and
   avoids introducing a new typed prop axis that would conflict with the
   `<FieldGroup width="…">` direction tracked in ADR-032 (cross-control
   field-width parity, deferred to v5.2+). */
const WRAPPER_LAYOUT_KEYS = [
  'width',
  'minWidth',
  'maxWidth',
  'inlineSize',
  'minInlineSize',
  'maxInlineSize',
  'flex',
  'flexBasis',
  'flexGrow',
  'flexShrink',
] as const;

function splitLayoutStyle(style: CSSProperties | undefined): {
  wrapper: CSSProperties | undefined;
  button: CSSProperties | undefined;
} {
  if (!style) return { wrapper: undefined, button: undefined };
  let wrapper: CSSProperties | undefined;
  let button: CSSProperties | undefined;
  for (const key of Object.keys(style) as Array<keyof CSSProperties>) {
    const value = style[key];
    if (value === undefined) continue;
    if ((WRAPPER_LAYOUT_KEYS as readonly string[]).includes(key as string)) {
      (wrapper ??= {})[key] = value as never;
    } else {
      (button ??= {})[key] = value as never;
    }
  }
  return { wrapper, button };
}

/** Merge an external ref with an internal ref. */
function useMergedRef<T>(
  externalRef: React.ForwardedRef<T>,
  internalRef: React.RefObject<T | null>,
): React.RefCallback<T> {
  return React.useCallback(
    (node: T | null) => {
      (internalRef as React.MutableRefObject<T | null>).current = node;
      if (typeof externalRef === 'function') {
        externalRef(node);
      } else if (externalRef) {
        (externalRef as React.MutableRefObject<T | null>).current = node;
      }
    },
    [externalRef, internalRef],
  );
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    value,
    onChange,
    onValueChange,
    options,
    placeholder,
    className,
    style,
    size = 'md',
    search = false,
    searchPlaceholder = 'Search...',
    emptyMessage = 'No results',
    error,
    disabled,
    name,
    unsafe_className,
    unsafe_style,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const [showFocusedOption, setShowFocusedOption] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchFocused, setSearchFocused] = React.useState(false);
  const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const mergedRef = useMergedRef(ref, triggerRef);
  const listboxId = React.useId();

  /* Mount-only dev-warn; see Input.tsx for the rationale. */
  React.useEffect(() => {
    if (!IS_PROD && value === undefined) devWarnValueUndefined('Select');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? placeholder ?? '';
  const selectedIndex = options.findIndex((o) => o.value === value);
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const visibleOptions = React.useMemo(
    () =>
      options
        .map((option, index) => ({ option, index }))
        .filter(
          ({ option }) =>
            option._action ||
            !normalizedSearch ||
            option.label.toLowerCase().includes(normalizedSearch),
        ),
    [normalizedSearch, options],
  );
  const visibleIndices = React.useMemo(
    () => visibleOptions.map(({ index }) => index),
    [visibleOptions],
  );
  const firstVisibleIndex = visibleIndices[0] ?? -1;
  const lastVisibleIndex = visibleIndices[visibleIndices.length - 1] ?? -1;

  const moveVisibleFocus = React.useCallback(
    (direction: 1 | -1, fallbackIndex = firstVisibleIndex) => {
      if (visibleIndices.length === 0) {
        setFocusedIndex(-1);
        return;
      }
      const currentVisibleIndex = visibleIndices.indexOf(focusedIndex);
      if (currentVisibleIndex === -1) {
        setFocusedIndex(fallbackIndex >= 0 ? fallbackIndex : firstVisibleIndex);
        return;
      }
      const nextVisibleIndex =
        (currentVisibleIndex + direction + visibleIndices.length) % visibleIndices.length;
      setFocusedIndex(visibleIndices[nextVisibleIndex] ?? firstVisibleIndex);
    },
    [firstVisibleIndex, focusedIndex, visibleIndices],
  );

  /* ── Open / Close helpers ──────────────────────────── */

  const openDropdown = React.useCallback(
    (initialIndex?: number, showVisualFocus = false) => {
      if (disabled) return;
      setTriggerWidth(triggerRef.current?.offsetWidth ?? null);
      setOpen(true);
      setShowFocusedOption(showVisualFocus);
      // Focus the selected item, or first item, or use the provided index
      const idx = initialIndex ?? (selectedIndex >= 0 ? selectedIndex : 0);
      setFocusedIndex(idx);
    },
    [disabled, selectedIndex],
  );

  const closeDropdown = React.useCallback((returnFocus = true) => {
    setOpen(false);
    setFocusedIndex(-1);
    setShowFocusedOption(false);
    setSearchQuery('');
    setSearchFocused(false);
    if (returnFocus && triggerRef.current?.isConnected) {
      triggerRef.current.focus();
    }
  }, []);

  const selectOption = React.useCallback(
    (optionValue: string) => {
      if (disabled) {
        closeDropdown(false);
        return;
      }
      onValueChange?.(optionValue);
      onChange?.(createSelectChangeEvent(optionValue, name));
      closeDropdown(true);
    },
    [disabled, onValueChange, onChange, name, closeDropdown],
  );

  React.useEffect(() => {
    if (disabled && open) closeDropdown(false);
  }, [disabled, open, closeDropdown]);

  /* ── Focus management ──────────────────────────────── */

  /* v5.1 (consumer feedback #7, Cabinet audit 2026-05-20): the trigger
     button keeps DOM focus throughout the open/closed lifecycle. The
     listbox div does NOT receive focus — `aria-activedescendant` on the
     trigger (combobox) handles AT announcements of the virtually-focused
     option. The previous focus-the-listbox-on-open pattern raced with
     the click event in real browsers (JSDOM tests passed because the
     race didn't manifest there): after mouse open, focus appeared to
     land on the listbox but in practice the trigger retained it and
     arrow keys went nowhere. Routing arrows / typeahead / Enter / Escape
     through `handleTriggerKeyDown` eliminates the race entirely. APG
     Combobox + Listbox pattern, "focus stays in combobox" variant. */

  /* ── Type-ahead ────────────────────────────────────── */

  const typeaheadRef = React.useRef('');
  const typeaheadTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTypeahead = React.useCallback(
    (char: string) => {
      if (typeaheadTimerRef.current) clearTimeout(typeaheadTimerRef.current);
      typeaheadRef.current += char.toLowerCase();

      const search = typeaheadRef.current;
      const match = visibleOptions.find(({ option }) =>
        option.label.toLowerCase().startsWith(search),
      );
      if (match) {
        setShowFocusedOption(true);
        setFocusedIndex(match.index);
      }

      typeaheadTimerRef.current = setTimeout(() => {
        typeaheadRef.current = '';
      }, 500);
    },
    [visibleOptions],
  );

  React.useEffect(() => {
    if (!open || !search) return;
    if (visibleIndices.length === 0) {
      setFocusedIndex(-1);
      return;
    }
    if (!visibleIndices.includes(focusedIndex)) {
      setFocusedIndex(firstVisibleIndex);
    }
  }, [firstVisibleIndex, focusedIndex, open, search, visibleIndices]);

  /* ── Keyboard handlers ─────────────────────────────── */

  /* Single key handler on the trigger button (combobox). Handles both
     open and closed states — see "Focus management" note above for the
     APG pattern this implements. */
  const handleTriggerKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          if (!open) {
            openDropdown(selectedIndex >= 0 ? selectedIndex : 0, true);
          } else {
            setShowFocusedOption(true);
            moveVisibleFocus(1);
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          if (!open) {
            openDropdown(lastVisibleIndex >= 0 ? lastVisibleIndex : options.length - 1, true);
          } else {
            setShowFocusedOption(true);
            moveVisibleFocus(-1, lastVisibleIndex);
          }
          break;
        }
        case 'Home': {
          if (!open) break;
          e.preventDefault();
          setShowFocusedOption(true);
          setFocusedIndex(firstVisibleIndex);
          break;
        }
        case 'End': {
          if (!open) break;
          e.preventDefault();
          setShowFocusedOption(true);
          setFocusedIndex(lastVisibleIndex);
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          if (!open) {
            openDropdown(undefined, true);
          } else if (focusedIndex >= 0 && focusedIndex < options.length) {
            selectOption(options[focusedIndex].value);
          }
          break;
        }
        case 'Escape': {
          if (!open) break;
          e.preventDefault();
          closeDropdown(true);
          break;
        }
        case 'Tab': {
          // Let browser handle natural tab order; close on tab-away.
          if (open) closeDropdown(false);
          break;
        }
        default: {
          // Type-ahead: only when open, single printable character.
          if (open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            handleTypeahead(e.key);
          }
          break;
        }
      }
    },
    [
      disabled,
      open,
      openDropdown,
      closeDropdown,
      selectedIndex,
      options,
      focusedIndex,
      selectOption,
      handleTypeahead,
      firstVisibleIndex,
      lastVisibleIndex,
      moveVisibleFocus,
    ],
  );

  const handleSearchKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setShowFocusedOption(true);
          moveVisibleFocus(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setShowFocusedOption(true);
          moveVisibleFocus(-1, lastVisibleIndex);
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < options.length) {
            selectOption(options[focusedIndex].value);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown(true);
          break;
        case 'Tab':
          closeDropdown(false);
          break;
      }
    },
    [
      closeDropdown,
      disabled,
      focusedIndex,
      lastVisibleIndex,
      moveVisibleFocus,
      options,
      selectOption,
    ],
  );

  /* ── Scroll focused option into view ───────────────── */

  React.useEffect(() => {
    if (!open || !showFocusedOption || focusedIndex < 0 || !listRef.current) return;
    const optionEl = listRef.current.querySelector<HTMLElement>(
      `[data-select-index="${focusedIndex}"]`,
    );
    if (typeof optionEl?.scrollIntoView === 'function') {
      optionEl.scrollIntoView({ block: 'nearest' });
    }
  }, [open, showFocusedOption, focusedIndex]);

  /* ── Render ────────────────────────────────────────── */

  /* v5.1.x regression hotfix — see `splitLayoutStyle` docblock above.
     The merge order matches the v5.1 behavior (`unsafe_style` wins on
     collision, per ADR-017 escape-hatch precedence): style is the
     low-precedence base, unsafe_style overlays it. We split AFTER the
     merge so adopters who pass `width` on either prop reach the
     wrapper, with the same collision semantics as before. */
  const mergedStyle = unsafe_style ? { ...style, ...unsafe_style } : style;
  const { wrapper: wrapperStyle, button: buttonStyle } = splitLayoutStyle(mergedStyle);
  const activeDescendant =
    open && focusedIndex >= 0 ? `${listboxId}-opt-${focusedIndex}` : undefined;

  return (
    <div className="j-select-wrapper" style={wrapperStyle}>
      <button
        ref={mergedRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={!searchFocused ? activeDescendant : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        className={cx(
          'j-select-standalone',
          size === 'sm' && 'j-select-sm',
          size === 'lg' && 'j-select-lg',
          error && 'j-select-error',
          className,
          unsafe_className,
        )}
        style={buttonStyle}
        onClick={() => {
          if (disabled) return;
          if (open) {
            closeDropdown(true);
          } else {
            openDropdown();
          }
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={cx('j-select-value', !selectedOption && 'j-select-placeholder')}>
          {displayLabel}
        </span>
      </button>

      {name != null && <input type="hidden" name={name} value={value ?? ''} disabled={disabled} />}

      <Popover
        open={open}
        onClose={() => closeDropdown(false)}
        anchorRef={triggerRef}
        placement="bottom"
        align="start"
        panelRole={null}
        mobilePresentation="sheet"
        className="j-menu j-select-listbox"
        // eslint-disable-next-line @atelierpx/jacquard/no-experimental-without-prefix -- internal Popover sizing handoff; Select owns this measured min-width.
        unsafe_style={
          triggerWidth ? { minWidth: Math.max(triggerWidth, search ? 320 : 0) } : undefined
        }
      >
        {search && (
          <label className="j-select-search">
            <JIcon intent="search" decorative size={14} strokeWidth={2} />
            <input
              type="text"
              role="searchbox"
              value={searchQuery}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={listboxId}
              aria-activedescendant={searchFocused ? activeDescendant : undefined}
              autoComplete="off"
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </label>
        )}
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className="j-select-options"
        >
          {visibleOptions.map(({ option: opt, index: i }) => (
            <React.Fragment key={opt.value}>
              {opt._dividerBefore && <div className="j-menu-divider" role="separator" />}
              {/* Keyboard is handled on the combobox trigger via aria-activedescendant
                  (consumer feedback #7, 2026-05-20 — see "Focus management" comment
                  above). Options only need click/pointer handlers per APG. */}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
              <div
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={opt.value === value}
                data-select-index={i}
                className={cx(
                  'j-menu-item',
                  'j-select-option',
                  opt._action && 'j-select-option-action',
                  opt.value === value && 'j-select-option-selected',
                  showFocusedOption && i === focusedIndex && 'j-select-option-focused',
                )}
                onClick={() => selectOption(opt.value)}
                onMouseEnter={() => {
                  setShowFocusedOption(true);
                  setFocusedIndex(i);
                }}
              >
                <span className="j-select-option-check" aria-hidden="true">
                  {opt._action ? (
                    <JIcon intent="plus" decorative size={12} strokeWidth={2.5} />
                  ) : (
                    opt.value === value && (
                      <JIcon intent="check" decorative size={12} strokeWidth={2.5} />
                    )
                  )}
                </span>
                {opt.icon && (
                  <span className="j-select-option-icon" aria-hidden="true">
                    {opt.icon}
                  </span>
                )}
                <span className="j-select-option-label">{opt.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
        {search && visibleOptions.length === 0 && (
          <div className="j-select-empty" role="status">
            {emptyMessage}
          </div>
        )}
      </Popover>
    </div>
  );
});
